/**
 * PageSkeleton — the loading layout of every Creator Studio surface.
 *
 * "Make the loading screen load the page layout, and show that the page
 * layout is loading with the content that's going to be there." Each surface
 * gets the shell it will render into (CreatorStudioPage: chrome or back bar,
 * `.cs-page-container` at the page's own max-width, the title block) with its
 * cards drawn as grey shimmering shapes — no spinner, no copy. root.tsx draws
 * one for the destination while a navigation is in flight; profile.tsx and
 * savedIdeas.tsx draw theirs while their client-side fetch runs.
 *
 * The layout is picked from the pathname (`resolveSkeletonKind`), most
 * specific route first; anything unknown gets the generic card column. Widths
 * and grid classes are the ones each route passes to the shell, so the
 * placeholder and the page it stands in for share gutters and breakpoints.
 * Shapes live in skeletonShapes.tsx, styles in app/styles/creator-skeleton.css.
 * Nothing here reads the viewport.
 */
import { Skeleton, SkeletonText } from '~/components/ui/creator_studio/skeleton';
import {
  Card, Cards, Cols, IconCard, Kpis, Pills, Rows, SavedCardSkeleton, Shell, Stack, TextCard, cssVars } from
'~/components/ui/creator_studio/skeletonShapes';

export { SavedCardSkeleton, SkeletonChrome } from '~/components/ui/creator_studio/skeletonShapes';

export type SkeletonKind =
'rewards' | 'browse' | 'detail' | 'applications' | 'payouts' | 'saved' | 'brands' |
'dna' | 'inbox' | 'track' | 'coaching' | 'profile' | 'feed' | 'settings' | 'generic';

/** Most specific first: a detail route must not fall through to its list. */
const ROUTE_SHAPES: ReadonlyArray<readonly [RegExp, SkeletonKind]> = [
[/^\/creator_studio\/content_rewards\/browse\/[^/]+$/, 'detail'],
[/^\/creator_studio\/content_rewards\/browse$/, 'browse'],
[/^\/creator_studio\/content_rewards\/[^/]+$/, 'detail'],
[/^\/creator_studio\/content_rewards$/, 'rewards'],
[/^\/creator_studio\/applications$/, 'applications'],
[/^\/creator_studio\/payouts$/, 'payouts'],
[/^\/creator_studio\/saved$/, 'saved'],
[/^\/creator_studio\/brands$/, 'brands'],
[/^\/creator_studio\/dna$/, 'dna'],
[/^\/creator_studio\/inbox$/, 'inbox'],
[/^\/creator_studio\/track\/[^/]+$/, 'coaching'],
[/^\/creator_studio\/track$/, 'track'],
[/^\/creator_studio\/settings$/, 'settings'],
[/^\/profile$/, 'profile'],
[/^\/creator_studio$/, 'feed']];


const normalise = (pathname: string) => pathname.replace(/\/+$/, '') || '/';

export function resolveSkeletonKind(pathname: string): SkeletonKind {
  const path = normalise(pathname);
  const hit = ROUTE_SHAPES.find(([pattern]) => pattern.test(path));
  return hit ? hit[1] : 'generic';
}

/** The surfaces the navigation skeleton covers: the studio and the profile home. */
export function isCreatorStudioPath(pathname: string | null | undefined): boolean {
  if (!pathname) return false;
  const path = normalise(pathname);
  return path === '/profile' || path === '/creator_studio' || path.startsWith('/creator_studio/');
}

const TWO_EQUAL = 'minmax(0, 1fr) minmax(0, 1fr)';

/** Hub: three KPI tiles (2×2 on the phone), the tab pills, the reward cards. */
function RewardsSkeleton() {
  return (
    <Shell kind="rewards" maxWidth={1620}>
      <Kpis count={3} cols={3} className="cs-reward-kpis cs-hide-mobile cs-skel-only-desktop" />
      <Kpis count={4} cols={2} className="cs-reward-kpis cs-hide-desktop cs-skel-only-mobile" />
      <Pills />
      <Cards count={4} min={400} className="cs-reward-grid" />
    </Shell>);

}

/** Browse: stat card in the header, pills, the browse card grid. */
function BrowseSkeleton() {
  return (
    <Shell kind="browse" maxWidth={1620} header="aside">
      <Pills />
      <Cards count={4} min={420} className="cs-reward-grid cs-reward-grid--browse" />
    </Shell>);

}

/** Reward / campaign detail: hero card, then two columns of cards. */
function DetailSkeleton() {
  return (
    <Shell kind="detail" maxWidth={1500} header="none">
      <div className="cs-skel-stack">
        <Card>
          <div className="cs-skel-row">
            <Skeleton width={56} height={56} radius={16} />
            <SkeletonText lines={2} lineHeight={16} widths={['50%', '30%']} />
          </div>
          <div className="cs-skel-row">
            <Skeleton width={96} height={28} radius={6} />
            <Skeleton width={96} height={28} radius={6} />
            <Skeleton width={96} height={28} radius={6} />
          </div>
        </Card>
        <Cols cols={TWO_EQUAL} gap="18px">
          <Stack count={2} />
          <Stack count={2} />
        </Cols>
      </div>
    </Shell>);

}

/** Applications: count chips, then table rows. */
function ApplicationsSkeleton() {
  return (
    <Shell kind="applications" maxWidth={1400} header="aside">
      <Pills count={4} />
      <Rows count={5} />
    </Shell>);

}

/** Payouts: 1.25fr / 1fr. */
function PayoutsSkeleton() {
  return (
    <Shell kind="payouts" maxWidth={1240}>
      <Cols cols="minmax(0, 1.25fr) minmax(0, 1fr)" gap="20px" className="cs-payouts-body">
        <Stack count={2} className="cs-payouts-main" />
        <Stack count={2} className="cs-payouts-rail" />
      </Cols>
    </Shell>);

}

/** Saved: stat card, brand pills, the saved card grid (rows on the phone). */
function SavedSkeleton() {
  return (
    <Shell kind="saved" maxWidth={1400} header="aside">
      <Pills />
      <div className="cs-saved-grid cs-skel-cards" style={cssVars({ '--cs-skel-min': '320px' })}>
        {Array.from({ length: 6 }, (_, i) => <SavedCardSkeleton key={i} />)}
      </div>
    </Shell>);

}

/** Brands: stat card, 1.55fr / 1fr of brand cards. */
function BrandsSkeleton() {
  return (
    <Shell kind="brands" maxWidth={1620} header="aside">
      <Cols cols="minmax(0, 1.55fr) minmax(0, 1fr)" gap="22px" className="cs-brands-grid">
        <div className="cs-skel-stack">
          <IconCard />
          <IconCard />
          <IconCard />
        </div>
        <Stack count={2} className="cs-brands-side" />
      </Cols>
    </Shell>);

}

/** DNA: the black hero, three cards, then 1fr / 1.35fr. */
function DnaSkeleton() {
  return (
    <Shell kind="dna" maxWidth={1620}>
      <div className="cs-skel-stack">
        <Card dark minHeight={180}>
          <Skeleton width="35%" height={22} radius={6} />
          <SkeletonText lines={2} />
          <div className="cs-skel-row">
            <Skeleton width={88} height={28} radius={6} />
            <Skeleton width={88} height={28} radius={6} />
            <Skeleton width={88} height={28} radius={6} />
          </div>
        </Card>
        <Cols cols="repeat(3, minmax(0, 1fr))" gap="18px" className="cs-dna-row cs-dna-row-3">
          <TextCard lines={3} />
          <TextCard lines={3} />
          <TextCard lines={3} />
        </Cols>
        <Cols cols="minmax(0, 1fr) minmax(0, 1.35fr)" gap="18px" className="cs-dna-row cs-dna-row-2">
          <TextCard lines={4} />
          <TextCard lines={4} />
        </Cols>
      </div>
    </Shell>);

}

/** Inbox: 1fr / 1.15fr — message rows, then rail cards. */
function InboxSkeleton() {
  return (
    <Shell kind="inbox" maxWidth={1500}>
      <Cols cols="minmax(0, 1fr) minmax(0, 1.15fr)" gap="20px" className="inbox-layout">
        <Rows count={5} />
        <Stack count={2} minHeight={160} />
      </Cols>
    </Shell>);

}

/** Track: four KPI tiles, the work table beside the coherence card. */
function TrackSkeleton() {
  return (
    <Shell kind="track" maxWidth={1620} header="aside">
      <Kpis count={4} cols={4} className="track-kpis" />
      <Cols cols="minmax(0, 1.5fr) minmax(0, 1fr)" gap="22px" className="track-body">
        <Rows count={5} />
        <Card minHeight={220}>
          <Skeleton width="40%" height={18} radius={6} />
          <div className="cs-skel-row">
            <Skeleton width={96} height={96} radius="50%" />
            <SkeletonText lines={3} />
          </div>
        </Card>
      </Cols>
    </Shell>);

}

/** Coaching (track/:videoId): hero + accordion rows, and the rail. */
function CoachingSkeleton() {
  return (
    <Shell kind="coaching" maxWidth={1500} header="none">
      <Cols cols="minmax(0, 1.45fr) minmax(0, 1fr)" gap="22px" className="cs-coaching-layout">
        <div className="cs-skel-stack cs-coaching-main">
          <Card>
            <div className="cs-skel-row">
              <Skeleton width={64} height={88} radius={12} />
              <SkeletonText lines={3} widths={['55%', '90%', '35%']} />
            </div>
          </Card>
          <Rows count={3} />
        </div>
        <Stack count={2} className="cs-coaching-side" />
      </Cols>
    </Shell>);

}

/** Profile home: hero, two cards side by side, the four link cards as 2×2. */
function ProfileSkeleton() {
  return (
    <Shell kind="profile" maxWidth={1400} header="none">
      <div className="cs-skel-profile">
        <Card className="cs-skel-profile-hero">
          <div className="cs-skel-row">
            <Skeleton width={72} height={72} radius="50%" />
            <SkeletonText lines={2} lineHeight={14} widths={['38%', '24%']} />
            <Skeleton className="cs-skel-only-desktop cs-hide-mobile" width={120} height={36} radius={60} />
          </div>
          <div className="cs-skel-row">
            <Skeleton width={84} height={26} radius={60} />
            <Skeleton width={72} height={26} radius={60} />
            <Skeleton width={96} height={26} radius={60} />
          </div>
        </Card>
        <TextCard lines={3} minHeight={160} />
        <TextCard lines={3} minHeight={160} />
        <div className="cs-skel-profile-links">
          {Array.from({ length: 4 }, (_, i) =>
          <Card key={i}>
              <div className="cs-skel-row">
                <Skeleton width={40} height={40} radius={12} />
                <SkeletonText lines={2} lineHeight={11} widths={['55%', '80%']} />
              </div>
            </Card>
          )}
        </div>
      </div>
    </Shell>);

}

/** The feed: rail / idea card / context rail on desktop, a card stack on the phone. */
function FeedSkeleton() {
  return (
    <Shell kind="feed" maxWidth={1600} header="none">
      <div className="cs-skel-feed">
        <div className="cs-skel-stack cs-skel-only-desktop cs-hide-mobile">
          <TextCard lines={2} />
          <TextCard lines={3} />
        </div>
        <div className="cs-skel-feed-main">
          <Card minHeight={520}>
            <div className="cs-skel-row">
              <Skeleton width={44} height={44} radius="50%" />
              <SkeletonText lines={2} widths={['45%', '30%']} />
            </div>
            <Skeleton height={240} radius={16} />
            <SkeletonText lines={3} />
          </Card>
          <Stack count={2} />
        </div>
      </div>
    </Shell>);

}

/** Settings: two columns of cards (one on the phone). */
function SettingsSkeleton() {
  return (
    <Shell kind="settings" maxWidth={1400}>
      <Cols cols={TWO_EQUAL} gap="20px" className="cs-settings-grid">
        <div className="cs-skel-stack cs-settings-col">
          <Card>
            <div className="cs-skel-row">
              <Skeleton width={56} height={56} radius="50%" />
              <SkeletonText lines={2} widths={['40%', '25%']} />
            </div>
          </Card>
          <TextCard lines={4} />
        </div>
        <Stack count={2} className="cs-settings-col" />
      </Cols>
    </Shell>);

}

function GenericSkeleton() {
  return (
    <Shell kind="generic" maxWidth={1400}>
      <Stack count={3} />
    </Shell>);

}

const PAGES: Record<SkeletonKind, () => JSX.Element> = {
  rewards: RewardsSkeleton,
  browse: BrowseSkeleton,
  detail: DetailSkeleton,
  applications: ApplicationsSkeleton,
  payouts: PayoutsSkeleton,
  saved: SavedSkeleton,
  brands: BrandsSkeleton,
  dna: DnaSkeleton,
  inbox: InboxSkeleton,
  track: TrackSkeleton,
  coaching: CoachingSkeleton,
  profile: ProfileSkeleton,
  feed: FeedSkeleton,
  settings: SettingsSkeleton,
  generic: GenericSkeleton
};

export function PageSkeleton({ pathname }: {pathname: string;}) {
  const Page = PAGES[resolveSkeletonKind(pathname)];
  return <Page />;
}

export default PageSkeleton;